import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { EnvironmentService } from '@nomoh-monorepo/shared-data-access-environment';
import {
  ICreateDesconto,
  IDesconto,
  IDescontoFiltro,
  IDescontoResponse,
  IPagerSettings,
  IUpdateDesconto,
} from '@nomoh-monorepo/shared-types';
import { firstValueFrom } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class DescontosDataService {
  private http = inject(HttpClient);
  private env = inject(EnvironmentService);
  private baseUrl = `${this.env.getApiUrl('nomoh')}/desconto`;

  // ========================================
  // SIGNALS PRIVADOS (estado interno)
  // ========================================
  private _descontos = signal<IDesconto[]>([]);
  private _cuponsValidos = signal<IDesconto[]>([]);
  private _descontoSelecionado = signal<IDesconto | null>(null);
  private _loading = signal<boolean>(false);
  private _erro = signal<string | null>(null);
  private _paginacao = signal<IPagerSettings>({
    total: 0,
    offSet: 0,
    limit: 10,
    param: '',
    ordenacao: 0,
  });
  private _filtros = signal<Omit<IDescontoFiltro, 'page' | 'limit'>>({
    descricao: undefined,
    token: undefined,
    tipo: undefined,
  });

  // ========================================
  // SIGNALS PÚBLICOS (readonly)
  // ========================================
  $descontos = this._descontos.asReadonly();
  $cuponsValidos = this._cuponsValidos.asReadonly();
  $descontoSelecionado = this._descontoSelecionado.asReadonly();
  $loading = this._loading.asReadonly();
  $erro = this._erro.asReadonly();
  $paginacao = this._paginacao.asReadonly();
  $filtros = this._filtros.asReadonly();

  // ========================================
  // COMPUTED SIGNALS (estado derivado)
  // ========================================
  $temProxPagina = computed(() => {
    const pag = this.$paginacao();
    const offSet = pag.offSet ?? 0;
    const limit = pag.limit ?? 10;
    const total = pag.total ?? 0;
    return offSet + limit < total;
  });

  $temPagAnterior = computed(() => {
    const offSet = this.$paginacao().offSet ?? 0;
    return offSet > 0;
  });

  $paginaAtual = computed(() => {
    const pag = this.$paginacao();
    const offSet = pag.offSet ?? 0;
    const limit = pag.limit ?? 10;
    return Math.floor(offSet / limit) + 1;
  });

  $totalPaginas = computed(() => {
    const pag = this.$paginacao();
    const total = pag.total ?? 0;
    const limit = pag.limit ?? 10;
    return Math.ceil(total / limit);
  });

  $infoTotal = computed(() => {
    const pag = this.$paginacao();
    const offSet = pag.offSet ?? 0;
    const limit = pag.limit ?? 10;
    const total = pag.total ?? 0;
    const inicio = offSet + 1;
    const fim = Math.min(offSet + limit, total);
    return `${inicio}-${fim} de ${total}`;
  });

  $hasDescontos = computed(() => this.$descontos().length > 0);
  $isEmpty = computed(() => !this.$loading() && this.$descontos().length === 0);

  $descontosAtivos = computed(() => this.$descontos().filter((d) => d.ativo));

  $descontosInativos = computed(() =>
    this.$descontos().filter((d) => !d.ativo)
  );

  // ========================================
  // MÉTODOS DE ESTADO
  // ========================================
  setDescontos(response: IDescontoResponse) {
    this._descontos.set(response.data);
    this._paginacao.update((current) => ({
      ...current,
      total: response.total,
    }));
  }

  setLoading(loading: boolean) {
    this._loading.set(loading);
  }

  setErro(erro: string | null) {
    this._erro.set(erro);
  }

  setFiltros(filtros: Partial<Omit<IDescontoFiltro, 'page' | 'limit'>>) {
    this._filtros.update((current) => ({ ...current, ...filtros }));
  }

  resetFiltros() {
    this._filtros.set({});
  }

  selecionarDesconto(desconto: IDesconto | null): void {
    this._descontoSelecionado.set(desconto);
  }

  limparErro(): void {
    this._erro.set(null);
  }

  // ========================================
  // OPERAÇÕES HTTP + ESTADO
  // ========================================

  /**
   * Carrega descontos com filtros e paginação
   */
  async carregarDescontos(opcoes?: {
    offSet?: number;
    limit?: number;
    filtros?: Partial<Omit<IDescontoFiltro, 'page' | 'limit'>>;
  }): Promise<void> {
    this.setLoading(true);
    this.setErro(null);

    try {
      const pag = this.$paginacao();
      const filtrosAtuais = this.$filtros();

      const offSet = opcoes?.offSet ?? pag.offSet ?? 0;
      const limit = opcoes?.limit ?? pag.limit ?? 10;
      const page = Math.floor(offSet / limit) + 1;

      // Atualizar filtros se fornecidos
      if (opcoes?.filtros) {
        this.setFiltros(opcoes.filtros);
      }

      const filtroCompleto: IDescontoFiltro = {
        ...filtrosAtuais,
        ...(opcoes?.filtros || {}),
        page,
        limit,
      };

      const response = await firstValueFrom(
        this.http.post<IDescontoResponse>(
          `${this.baseUrl}/findAll`,
          filtroCompleto
        )
      );

      this.setDescontos(response);
      this._paginacao.update((current) => ({
        ...current,
        offSet,
        limit,
      }));
    } catch (error: any) {
      const mensagem = error.message || 'Erro ao carregar descontos';
      this.setErro(mensagem);
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Busca desconto por ID
   */
  async buscarPorId(id: string): Promise<void> {
    this.setLoading(true);
    this.setErro(null);

    try {
      const desconto = await firstValueFrom(
        this.http.get<IDesconto>(`${this.baseUrl}/${id}`)
      );
      this.selecionarDesconto(desconto);
    } catch (error: any) {
      const mensagem = error.message || 'Erro ao carregar desconto';
      this.setErro(mensagem);
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Cria novo desconto
   */
  async criar(data: ICreateDesconto): Promise<string> {
    this.setLoading(true);
    this.setErro(null);

    try {
      const response = await firstValueFrom(
        this.http.post<{ id: string }>(`${this.baseUrl}/create`, data)
      );
      const id = response.id;

      // Recarregar lista após criação
      await this.carregarDescontos();

      return id;
    } catch (error: any) {
      const mensagem = error.message || 'Erro ao criar desconto';
      this.setErro(mensagem);
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Atualiza desconto existente
   */
  async atualizar(id: string, data: IUpdateDesconto): Promise<void> {
    this.setLoading(true);
    this.setErro(null);

    try {
      await firstValueFrom(
        this.http.put<{ message: string }>(`${this.baseUrl}/${id}`, data)
      );

      // Recarregar lista após atualização
      await this.carregarDescontos();
    } catch (error: any) {
      const mensagem = error.message || 'Erro ao atualizar desconto';
      this.setErro(mensagem);
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Deleta desconto
   */
  async deletar(id: string): Promise<void> {
    this.setLoading(true);
    this.setErro(null);

    try {
      await firstValueFrom(
        this.http.delete<{ message: string }>(`${this.baseUrl}/${id}`)
      );

      // Recarregar lista após exclusão
      await this.carregarDescontos();
    } catch (error: any) {
      const mensagem = error.message || 'Erro ao deletar desconto';
      this.setErro(mensagem);
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Busca cupons válidos (ativos, não expirados, com usos disponíveis)
   */
  async buscarCuponsValidos(): Promise<void> {
    this.setLoading(true);
    this.setErro(null);

    try {
      const cupons = await firstValueFrom(
        this.http.get<IDesconto[]>(`${this.baseUrl}/validos`)
      );
      this._cuponsValidos.set(cupons);
    } catch (error: any) {
      const mensagem = error.message || 'Erro ao carregar cupons válidos';
      this.setErro(mensagem);
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  // ========================================
  // MÉTODOS DE PAGINAÇÃO
  // ========================================

  async proximaPagina(): Promise<void> {
    if (!this.$temProxPagina()) return;

    const pag = this.$paginacao();
    const novoOffSet = (pag.offSet ?? 0) + (pag.limit ?? 10);

    await this.carregarDescontos({ offSet: novoOffSet });
  }

  async paginaAnterior(): Promise<void> {
    if (!this.$temPagAnterior()) return;

    const pag = this.$paginacao();
    const novoOffSet = Math.max(0, (pag.offSet ?? 0) - (pag.limit ?? 10));

    await this.carregarDescontos({ offSet: novoOffSet });
  }

  async irParaPagina(pagina: number): Promise<void> {
    const limit = this.$paginacao().limit ?? 10;
    const offSet = (pagina - 1) * limit;

    await this.carregarDescontos({ offSet });
  }

  async alterarTamanhoPagina(limit: number): Promise<void> {
    await this.carregarDescontos({ offSet: 0, limit });
  }

  // ========================================
  // RESET STATE
  // ========================================
  resetState(): void {
    this._descontos.set([]);
    this._descontoSelecionado.set(null);
    this._loading.set(false);
    this._erro.set(null);
    this._paginacao.set({
      total: 0,
      offSet: 0,
      limit: 10,
      param: '',
      ordenacao: 0,
    });
    this._filtros.set({
      descricao: undefined,
      token: undefined,
      tipo: undefined,
    });
  }
}
